/**
 * Phase 26.2 — Admin promo code management.
 *
 * Single-tenant admin surface. The list / create / deactivate API
 * endpoints are gated server-side on the project owner's user id and
 * return 404 for anyone else; this page does a matching client-side
 * gate so non-admins see a 404 view rather than a partially-rendered
 * form that would error on first fetch.
 */
import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import {
  type AdminPromoCode,
  createAdminPromoCode,
  deactivateAdminPromoCode,
  listAdminPromoCodes,
} from '../utils/api';

const ADMIN_USER_ID = '316f4edc-c9ab-480f-b008-977d8f5415a2';

function NotFound() {
  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--tco-ink-2)',
        fontFamily: 'var(--tco-font-sans)',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, fontWeight: 700, marginBottom: 8 }}>
          404
        </div>
        <div style={{ fontSize: 14 }}>Page not found.</div>
      </div>
    </div>
  );
}

const TIER_OPTIONS: { value: 'promo' | 'paid'; label: string }[] = [
  { value: 'promo', label: 'Promo (Fleet beta)' },
  { value: 'paid', label: 'Paid (Fleet)' },
];

function formatDate(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
}

export default function AdminPage() {
  const user = useAuthStore((s) => s.user);
  const authInitialized = useAuthStore((s) => s.initialized);

  const [codes, setCodes] = useState<AdminPromoCode[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [newCode, setNewCode] = useState('');
  const [newTier, setNewTier] = useState<'promo' | 'paid'>('promo');
  const [newMaxUses, setNewMaxUses] = useState(1);
  const [newExpires, setNewExpires] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState<string | null>(null);

  const isAdmin = !!user && user.id === ADMIN_USER_ID;

  async function refresh() {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await listAdminPromoCodes();
      setCodes(rows);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load codes.');
      setCodes([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!authInitialized) return;
    if (!isAdmin) return;
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authInitialized, isAdmin]);

  if (!authInitialized) {
    return (
      <div style={{ padding: 24, color: 'var(--tco-ink-2)' }}>Loading…</div>
    );
  }

  if (!isAdmin) {
    return <NotFound />;
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    setCreateSuccess(null);
    const trimmed = newCode.trim().toUpperCase();
    if (!/^[A-Z0-9_]{4,20}$/.test(trimmed)) {
      setCreateError(
        'Code must be 4-20 characters, uppercase letters/digits/underscore only.',
      );
      return;
    }
    setCreating(true);
    try {
      await createAdminPromoCode({
        code: trimmed,
        tier_granted: newTier,
        max_uses: newMaxUses,
        expires_at: newExpires || null,
        notes: newNotes || null,
      });
      setCreateSuccess(`Created code ${trimmed}.`);
      setNewCode('');
      setNewExpires('');
      setNewNotes('');
      setNewMaxUses(1);
      void refresh();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create.');
    } finally {
      setCreating(false);
    }
  }

  async function handleDeactivate(id: string, code: string) {
    if (!window.confirm(`Deactivate ${code}? This cannot be undone.`)) return;
    try {
      await deactivateAdminPromoCode(id);
      void refresh();
    } catch (err) {
      window.alert(
        err instanceof Error ? err.message : 'Failed to deactivate.',
      );
    }
  }

  return (
    <div
      style={{
        padding: '24px',
        maxWidth: 1100,
        margin: '0 auto',
        fontFamily: 'var(--tco-font-sans)',
        color: 'var(--tco-ink)',
      }}
    >
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 18 }}>
        Admin — Promo Codes
      </h1>

      <section
        style={{
          background: 'var(--tco-surface)',
          border: '1px solid var(--tco-line)',
          borderRadius: 'var(--tco-radius)',
          padding: 18,
          marginBottom: 24,
        }}
      >
        <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
          Create new code
        </h2>
        <form onSubmit={handleCreate}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 12,
              alignItems: 'end',
            }}
          >
            <label style={{ display: 'block' }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--tco-ink-3)',
                  marginBottom: 4,
                }}
              >
                Code
              </span>
              <input
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                placeholder="EXAMPLE_2026"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid var(--tco-line)',
                  borderRadius: 'var(--tco-radius)',
                  background: 'var(--tco-bg)',
                  color: 'var(--tco-ink)',
                  fontFamily: 'inherit',
                }}
              />
            </label>
            <label style={{ display: 'block' }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--tco-ink-3)',
                  marginBottom: 4,
                }}
              >
                Tier
              </span>
              <select
                value={newTier}
                onChange={(e) =>
                  setNewTier(e.target.value as 'promo' | 'paid')
                }
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid var(--tco-line)',
                  borderRadius: 'var(--tco-radius)',
                  background: 'var(--tco-bg)',
                  color: 'var(--tco-ink)',
                  fontFamily: 'inherit',
                }}
              >
                {TIER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'block' }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--tco-ink-3)',
                  marginBottom: 4,
                }}
              >
                Max uses
              </span>
              <input
                type="number"
                min={1}
                value={newMaxUses}
                onChange={(e) =>
                  setNewMaxUses(Math.max(1, Number(e.target.value) || 1))
                }
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid var(--tco-line)',
                  borderRadius: 'var(--tco-radius)',
                  background: 'var(--tco-bg)',
                  color: 'var(--tco-ink)',
                  fontFamily: 'inherit',
                }}
              />
            </label>
            <label style={{ display: 'block' }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--tco-ink-3)',
                  marginBottom: 4,
                }}
              >
                Expires (optional)
              </span>
              <input
                type="date"
                value={newExpires}
                onChange={(e) => setNewExpires(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid var(--tco-line)',
                  borderRadius: 'var(--tco-radius)',
                  background: 'var(--tco-bg)',
                  color: 'var(--tco-ink)',
                  fontFamily: 'inherit',
                }}
              />
            </label>
            <label style={{ display: 'block', gridColumn: '1 / -1' }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: 'var(--tco-ink-3)',
                  marginBottom: 4,
                }}
              >
                Notes
              </span>
              <input
                value={newNotes}
                onChange={(e) => setNewNotes(e.target.value)}
                placeholder="Optional — what this code is for"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid var(--tco-line)',
                  borderRadius: 'var(--tco-radius)',
                  background: 'var(--tco-bg)',
                  color: 'var(--tco-ink)',
                  fontFamily: 'inherit',
                }}
              />
            </label>
          </div>
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="submit"
              disabled={creating}
              style={{
                background: 'var(--tco-ink)',
                color: 'var(--tco-bg)',
                border: '1px solid var(--tco-ink)',
                padding: '8px 16px',
                borderRadius: 'var(--tco-radius)',
                fontWeight: 600,
                cursor: creating ? 'not-allowed' : 'pointer',
                opacity: creating ? 0.6 : 1,
              }}
            >
              {creating ? 'Creating…' : 'Create code'}
            </button>
            {createError && (
              <span style={{ color: 'var(--tco-neg)', fontSize: 13 }}>
                {createError}
              </span>
            )}
            {createSuccess && (
              <span style={{ color: 'var(--tco-pos)', fontSize: 13 }}>
                {createSuccess}
              </span>
            )}
          </div>
        </form>
      </section>

      <section>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 8,
          }}
        >
          <h2 style={{ fontSize: 14, fontWeight: 600 }}>All codes</h2>
          <button
            type="button"
            onClick={() => void refresh()}
            style={{
              background: 'transparent',
              border: '1px solid var(--tco-line)',
              color: 'var(--tco-ink)',
              padding: '4px 12px',
              borderRadius: 'var(--tco-radius)',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
        {loadError && (
          <div style={{ color: 'var(--tco-neg)', marginBottom: 12 }}>
            {loadError}
          </div>
        )}
        <div
          style={{
            background: 'var(--tco-surface)',
            border: '1px solid var(--tco-line)',
            borderRadius: 'var(--tco-radius)',
            overflowX: 'auto',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontVariantNumeric: 'tabular-nums',
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ background: 'var(--tco-bg)' }}>
                {['Code', 'Tier', 'Uses', 'Expires', 'Notes', 'Status', 'Actions'].map(
                  (h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '10px 12px',
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: 'var(--tco-ink-3)',
                        borderBottom: '1px solid var(--tco-line)',
                      }}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      padding: '14px 12px',
                      color: 'var(--tco-ink-3)',
                    }}
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && codes && codes.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      padding: '14px 12px',
                      color: 'var(--tco-ink-3)',
                    }}
                  >
                    No codes yet.
                  </td>
                </tr>
              )}
              {!loading &&
                codes &&
                codes.map((row) => (
                  <tr key={row.id}>
                    <td
                      style={{
                        padding: '8px 12px',
                        borderBottom: '1px solid var(--tco-line)',
                        fontFamily: 'var(--tco-font-mono, monospace)',
                      }}
                    >
                      {row.code}
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--tco-line)' }}>
                      {row.tier_granted}
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--tco-line)' }}>
                      {row.use_count} / {row.max_uses}
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--tco-line)' }}>
                      {formatDate(row.expires_at)}
                    </td>
                    <td
                      style={{
                        padding: '8px 12px',
                        borderBottom: '1px solid var(--tco-line)',
                        color: 'var(--tco-ink-2)',
                      }}
                    >
                      {row.notes || ''}
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--tco-line)' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 600,
                          background: row.active
                            ? 'var(--tco-pos-soft, rgba(34,197,94,0.1))'
                            : 'var(--tco-line-soft, rgba(255,255,255,0.05))',
                          color: row.active
                            ? 'var(--tco-pos, #16a34a)'
                            : 'var(--tco-ink-3)',
                        }}
                      >
                        {row.active ? 'Active' : 'Exhausted'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--tco-line)' }}>
                      <button
                        type="button"
                        onClick={() => void handleDeactivate(row.id, row.code)}
                        disabled={!row.active}
                        style={{
                          background: 'transparent',
                          border: '1px solid var(--tco-line)',
                          color: row.active ? 'var(--tco-ink)' : 'var(--tco-ink-3)',
                          padding: '4px 10px',
                          borderRadius: 'var(--tco-radius)',
                          fontSize: 12,
                          cursor: row.active ? 'pointer' : 'not-allowed',
                        }}
                      >
                        Deactivate
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
